import { useState, useCallback, useEffect } from 'react';
import { resolveByPlatform } from '@i-ark/utils';
import { useFullscreenState, useChangeEffect } from '@utils/react';
import { iArkStore } from '../../../state/store.js';
import { transition } from '../../_content.style.js';
import { useShortcut } from '../../hook.use-shortcut.js';
import { getScrollAnchorPoint, applyScrollAnchorToSheet } from '../utils/apply-scroll-anchor-to-sheet.js';

const MAX_SHEET_WIDTH_PX = 2800;
const ZOOM_FACTOR = 1.25;
const TOUCH_FACTOR = 1.1;
let touchDownCache = new Map();
const touchMoveCache = new Map();
const useZoomActions = (context, containerRef) => {
  const dispatch = iArkStore.useDispatch();
  const {
    fullscreenEnabled
  } = useFullscreenState();
  const mode = fullscreenEnabled || context === "lightbox" ? "fullscreen" : "default";
  const [zoomContainerWidth, setZoomContainerWidth] = useState(960);
  const [currFactor, setCurrFactor] = useState(0);
  const minFactorLevel = 0;
  const maxFactorLevel = Math.max(8, getMaxFactorLevel(MAX_SHEET_WIDTH_PX / zoomContainerWidth));
  const zoomInEnabled = currFactor < maxFactorLevel;
  const zoomOutEnabled = currFactor > minFactorLevel;
  const zoomIn = useCallback(() => {
    if (!zoomInEnabled) {
      return;
    }
    const scrollAnchor = getScrollAnchorPoint(containerRef, mode);
    setCurrFactor(prev => {
      const next = prev + 1;
      dispatch(iArkStore.actions.setZoomLevel(1 * ZOOM_FACTOR ** next));
      return next;
    });
    requestAnimationFrame(() => {
      applyScrollAnchorToSheet(containerRef, mode, scrollAnchor);
    });
  }, [containerRef, dispatch, mode, zoomInEnabled]);
  const zoomOut = useCallback(() => {
    if (!zoomOutEnabled) {
      return;
    }
    const scrollAnchor = getScrollAnchorPoint(containerRef, mode);
    setCurrFactor(prev => {
      const next = prev - 1;
      dispatch(iArkStore.actions.setZoomLevel(1 * ZOOM_FACTOR ** next));
      return next;
    });
    requestAnimationFrame(() => {
      applyScrollAnchorToSheet(containerRef, mode, scrollAnchor);
    });
  }, [containerRef, dispatch, mode, zoomOutEnabled]);
  useEffect(() => {
    const container = mode === "fullscreen" ? containerRef.current?.querySelector(`.${transition}`) : containerRef.current;
    if (!container) {
      return;
    }
    setZoomContainerWidth(container.clientWidth);
    const observer = new ResizeObserver(() => {
      setZoomContainerWidth(container.clientWidth);
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [containerRef, mode]);
  useChangeEffect(() => {
    if (!fullscreenEnabled) {
      setCurrFactor(0);
      dispatch(iArkStore.actions.setZoomLevel(1));
    }
  }, [fullscreenEnabled]);
  useEffect(() => {
    if (mode !== "fullscreen") {
      return;
    }
    const handleTouchStart = evt => {
      Array.from(evt.changedTouches).forEach(touch => {
        touchDownCache.set(touch.identifier, touch);
        touchMoveCache.set(touch.identifier, touch);
      });
      if (evt.touches.length === 2) {
        evt.preventDefault();
      }
    };
    const handleTouchMove = evt => {
      Array.from(evt.changedTouches).forEach(touch => {
        if (touchMoveCache.has(touch.identifier)) {
          touchMoveCache.set(touch.identifier, touch);
        }
      });

      // bail out if user isn't using exactly two pointers for the pinching
      // gesture
      if (evt.touches.length !== 2) {
        return;
      }
      evt.preventDefault();

      // bail out if we don't know exactly two starting points for the user's
      // fingers
      if (touchDownCache.size !== 2) {
        return;
      }

      // calculate distance between touch start and current distance, so we can
      // determine desired scale
      const touchDownDistance = calculateTouchDistance(touchDownCache);
      const touchMoveDistance = calculateTouchDistance(touchMoveCache);
      const scale = touchMoveDistance / touchDownDistance;
      if (scale > TOUCH_FACTOR) {
        zoomIn();

        // reset touchDownCache, so that next time we zoom in/out is based on
        // the current touch distance
        touchDownCache = new Map(touchMoveCache);
      } else if (scale < 1 / TOUCH_FACTOR) {
        zoomOut();

        // reset touchDownCache, so that next time we zoom in/out is based on
        // the current touch distance
        touchDownCache = new Map(touchMoveCache);
      }
    };
    const handleTouchEnd = evt => {
      Array.from(evt.changedTouches).forEach(touch => {
        touchDownCache.delete(touch.identifier);
        touchMoveCache.delete(touch.identifier);
      });
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchmove", handleTouchMove, {
      passive: false
    });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [mode, zoomIn, zoomOut]);
  const resetZoomFactor = useCallback(evt => {
    evt.preventDefault();
    const scrollAnchor = getScrollAnchorPoint(containerRef, mode);
    setCurrFactor(() => {
      dispatch(iArkStore.actions.setZoomLevel(1 * ZOOM_FACTOR ** 0));
      return 0;
    });
    requestAnimationFrame(() => {
      applyScrollAnchorToSheet(containerRef, mode, scrollAnchor);
    });
  }, [containerRef, dispatch, mode]);
  useShortcut(resolveByPlatform({
    win: "Ctrl+0",
    mac: "Cmd+0"
  }), resetZoomFactor);
  return {
    zoomIn,
    zoomOut,
    zoomInEnabled: currFactor < maxFactorLevel,
    zoomOutEnabled: currFactor > minFactorLevel,
    zoomEnabled: true
  };
};
function calculateTouchDistance(touches) {
  const touchCoords = Array.from(touches).sort(([aTouchId], [bTouchId]) => aTouchId - bTouchId).map(([, evt]) => ({
    x: evt.pageX,
    y: evt.pageY
  }));
  return Math.sqrt(Math.abs((touchCoords[0]?.x ?? 0) - (touchCoords[1]?.x ?? 0)) ** 2 + Math.abs((touchCoords[0]?.y ?? 0) - (touchCoords[1]?.y ?? 0)) ** 2);
}
function getMaxFactorLevel(maxZoomLevel) {
  let maxFactor = 0;
  while (1 * ZOOM_FACTOR ** maxFactor < maxZoomLevel) {
    maxFactor++;
  }
  return maxFactor;
}

export { useZoomActions };
//# sourceMappingURL=useZoomActions.js.map
